import React from "react";
import { IDialogPromptSentence } from "../api/DialogPromptApi";
import { SentenceApi } from "../api/SentenceApi";
import { handleErrorMessage } from "../component/ErrorDialog";
import { pinyinForEditing } from "./pinyinForEditing";
import { keptReadings, LineReading, lineReadings } from "./pinyinRequests";

interface UseConversationPinyinProps {
    history: any;
    location: any;
    setErrorMessage: (msg: string) => void;
}

export function useConversationPinyin({ history, location, setErrorMessage }: UseConversationPinyinProps) {

    const [conversationText, setConversationText] = React.useState("");
    const [conversationPinyin, setConversationPinyin] = React.useState("");
    const [conversationPinyinList, setConversationPinyinList] = React.useState<string[]>([]);
    const [conversationErrorText, setConversationErrorText] = React.useState<string | null>(null);
    const fetchRequestIdRef = React.useRef(0);
    const validationRequestIdRef = React.useRef(0);
    const readingsRef = React.useRef<LineReading[]>([]);

    const clearConversationErrors = React.useCallback(() => {
        setConversationErrorText(null);
    }, []);

    const clearConversationFields = React.useCallback(() => {
        fetchRequestIdRef.current += 1;
        validationRequestIdRef.current += 1;
        readingsRef.current = [];
        setConversationText("");
        setConversationPinyin("");
        setConversationPinyinList([]);
        setConversationErrorText(null);
    }, []);

    const validatePinyin = React.useCallback(async (conversationText: string, updatedPinyin: string) => {
        const requestId = ++validationRequestIdRef.current;
        const lines = conversationText.split("\n");
        const pinyinLines = updatedPinyin.split("\n");
        try {
            const validationResults = await Promise.all(
                lines.map((line, index) => {
                    const pinyinRaw = pinyinLines.length > index ? pinyinLines[index] : "";
                    const pinyinList = pinyinRaw.trim().split(/\s+/).filter(Boolean);
                    return SentenceApi.isTextPinyinValid(line, pinyinList)
                        .then(isValid => ({ isValid: isValid.data, line, pinyinList }))
                        .catch(err => {
                            console.error("Validation error:", err);
                            return { isValid: false, line, pinyinList };
                        });
                })
            );

            if (requestId !== validationRequestIdRef.current) return;
            const firstInvalid = validationResults.find(result => !result.isValid);
            if (firstInvalid) {
                setConversationErrorText(`Invalid pinyin for: "${firstInvalid.line}" → "${firstInvalid.pinyinList.join(" ")}"`);
            } else {
                clearConversationErrors();
            }
        } catch (err) {
            if (requestId !== validationRequestIdRef.current) return;
            handleErrorMessage(err, setErrorMessage, history, location);
            setConversationErrorText("Unable to validate pinyin");
        }

    }, [clearConversationErrors, setErrorMessage, history, location]);

    const setConversationFields = React.useCallback((dialog_prompt_sentence_list: IDialogPromptSentence[]) => {
        const conversationTextList = dialog_prompt_sentence_list.map(item => item.sentence_text || "");
        const conversationText = conversationTextList.join("\n") // Convert array to newline-separated string
        setConversationText(conversationText);
        validationRequestIdRef.current += 1;
        readingsRef.current = [];

        setConversationPinyin("");
        setConversationPinyinList([]);
        setConversationErrorText(null);

        const loadNumericPinyin = async () => {
            const requestId = ++fetchRequestIdRef.current;
            try {
                const pinyinList = await Promise.all(
                    conversationTextList.map(async (line, index) => {
                        if (!line.trim()) return "";
                        return (await pinyinForEditing(line, dialog_prompt_sentence_list[index].sentence_text_pinyin)).join(" ");
                    })
                );

                if (requestId !== fetchRequestIdRef.current) return;
                readingsRef.current = lineReadings(conversationText, pinyinList.join("\n"));
                setConversationPinyin(pinyinList.join("\n"));
                setConversationPinyinList(pinyinList);
                validatePinyin(conversationText, pinyinList.join("\n"));
            } catch (err) {
                if (requestId !== fetchRequestIdRef.current) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                setConversationErrorText("Unable to fetch pinyin");
            }
        };
        loadNumericPinyin();

    }, [validatePinyin, setErrorMessage, history, location]);

    const handleConversationTextChange = React.useCallback(async (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const updatedText = e.target.value;
        const requestId = ++fetchRequestIdRef.current;
        validationRequestIdRef.current += 1;
        setConversationText(updatedText);
        const lines = updatedText.split("\n").map(line => line.trim()).filter(Boolean);
        const kept = keptReadings(readingsRef.current, lines);
        // A reading belongs to its text: a changed line has none until its suggestion comes, so none can be saved with it.
        const shown = kept.map(reading => reading?.join(" ") ?? "");
        setConversationPinyin(shown.join("\n"));
        setConversationPinyinList(shown);

        try {
            const pinyinList = await Promise.all(
                lines.map(async (line, index) => (await pinyinForEditing(line, kept[index])).join(" "))
            );

            if (requestId !== fetchRequestIdRef.current) return;
            readingsRef.current = lineReadings(updatedText, pinyinList.join("\n"));
            setConversationPinyin(pinyinList.join("\n"));
            setConversationPinyinList(pinyinList)
            // A kept reading may be one typed here, so it says nothing about the lines being valid; a suggestion is.
            if (kept.some(reading => reading?.length)) validatePinyin(lines.join("\n"), pinyinList.join("\n"));
            else setConversationErrorText(null);
        } catch (err) {
            if (requestId !== fetchRequestIdRef.current) return;
            handleErrorMessage(err, setErrorMessage, history, location);
            setConversationErrorText("Unable to fetch pinyin");
        }
    }, [validatePinyin, setErrorMessage, history, location]);

    const handleConversationPinyinChange = React.useCallback(async (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const updatedPinyin = e.target.value;
        fetchRequestIdRef.current += 1;
        readingsRef.current = lineReadings(conversationText, updatedPinyin);
        setConversationPinyin(updatedPinyin);
        const updatedPinyinList = updatedPinyin.split("\n").map(line => line.trim()).filter(Boolean);
        setConversationPinyinList(updatedPinyinList);
        validatePinyin(conversationText, updatedPinyin)
    }, [validatePinyin, conversationText]);

    return {
        conversationText,
        conversationPinyin,
        conversationPinyinList,
        conversationErrorText,
        setConversationFields,
        clearConversationFields,
        clearConversationErrors,
        handleConversationTextChange,
        handleConversationPinyinChange,
    };
}
